import React, { useEffect, useState, useMemo } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { api } from '@/api/client';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import {
  Printer, Plus, Edit, Trash, Search, Filter, Download, X, ChevronRight,
  ArrowLeft, Building2, Package, LayoutList, Users as UsersIcon
} from 'lucide-react';
import { printDocument, buildGRNContent } from '@/utils/printDocs';
import { GRNForm } from '@/components/forms/GRNForm';
import { useAuth } from '@/context/AuthContext';
import { PAYMENT_STATUSES } from '@/utils/helpers';
import { exportToCSV, CSV_COLUMNS } from '@/utils/exportUtils';
import { formatCurrency, useCurrency } from '@/hooks/useCurrency';

const grnSubtotal = (grn) => (grn.items || []).reduce((s, i) => s + ((i.cost || 0) * (i.qty || 0)), 0);
const grnUnits = (grn) => (grn.items || []).reduce((s, i) => s + (i.qty || 0), 0);
const getStatusVariant = (status) => status === 'Paid' ? 'success' : status === 'Overdue' ? 'danger' : 'warning';

export const GRNs = () => {
  const currency = useCurrency();
  const [grns, setGrns] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingGRN, setEditingGRN] = useState(null);
  const { isSuperAdmin } = useAuth();

  // View mode
  const [view, setView] = useState('suppliers'); // 'suppliers' | 'all' | 'supplier-detail'
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [supplierSearch, setSupplierSearch] = useState('');

  // All GRNs filters
  const [searchTerm, setSearchTerm] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [invoiceFilter, setInvoiceFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [createdByFilter, setCreatedByFilter] = useState('');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [gr, sr] = await Promise.all([api.getGRNs(), api.getSuppliers()]);
      const sorted = [...(gr.data || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setGrns(sorted);
      setSuppliers(sr.data || []);
    } catch (e) { console.error('Error loading GRN data', e); }
  };

  const handlePrint = (grn) => {
    const content = buildGRNContent({ grn });
    printDocument({ title: grn.grn_number || `GRN-${grn.id.slice(0, 8)}`, content });
  };
  const handleEdit = (grn) => { setEditingGRN(grn); setShowForm(true); };
  const handleNew = () => { setEditingGRN(null); setShowForm(true); };
  const handleDelete = async (grn) => {
    if (!window.confirm(`Delete ${grn.grn_number || 'this GRN'}?`)) return;
    try {
      await api.deleteGRN(grn.id);
      await loadAll();
    } catch (error) { alert(error?.response?.data?.detail || 'Failed to delete'); }
  };

  // === Aggregate suppliers with GRN counts (bucketed by supplier_id — falls back to supplier_name if missing) ===
  const supplierBuckets = useMemo(() => {
    // Map keyed by supplier_id if present, else normalized name
    const map = new Map();
    for (const g of grns) {
      const key = g.supplier_id || `NAME::${(g.supplier_name || '').trim().toLowerCase()}`;
      if (!map.has(key)) {
        const supplier = g.supplier_id ? suppliers.find(s => s.id === g.supplier_id) : null;
        map.set(key, {
          key,
          supplier_id: g.supplier_id || null,
          supplier_code: supplier?.supplier_code || null,
          supplier_name: supplier?.name || g.supplier_name || 'Unknown supplier',
          contact_person: supplier?.contact_person || '',
          phone: supplier?.phone || '',
          grn_count: 0,
          total_units: 0,
          total_value: 0,
          last_received: '',
        });
      }
      const row = map.get(key);
      row.grn_count += 1;
      row.total_units += grnUnits(g);
      row.total_value += grnSubtotal(g);
      const rd = g.received_date || '';
      if (rd && rd > row.last_received) row.last_received = rd;
    }
    // Include suppliers with zero GRNs so the master view is complete
    for (const s of suppliers) {
      if (!Array.from(map.values()).some(r => r.supplier_id === s.id)) {
        map.set(s.id, {
          key: s.id,
          supplier_id: s.id,
          supplier_code: s.supplier_code,
          supplier_name: s.name,
          contact_person: s.contact_person || '',
          phone: s.phone || '',
          grn_count: 0,
          total_units: 0,
          total_value: 0,
          last_received: '',
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => b.grn_count - a.grn_count || a.supplier_name.localeCompare(b.supplier_name));
  }, [grns, suppliers]);

  const filteredSupplierRows = useMemo(() => {
    const q = supplierSearch.trim().toLowerCase();
    if (!q) return supplierBuckets;
    return supplierBuckets.filter(r =>
      (r.supplier_name || '').toLowerCase().includes(q) ||
      (r.supplier_code || '').toLowerCase().includes(q) ||
      (r.contact_person || '').toLowerCase().includes(q) ||
      (r.phone || '').toLowerCase().includes(q)
    );
  }, [supplierBuckets, supplierSearch]);

  // === All-GRNs filters (view = 'all') ===
  const createdByOptions = useMemo(() => {
    return Array.from(new Set(grns.map(g => g.created_by_name).filter(Boolean))).sort();
  }, [grns]);
  const filteredGRNs = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    const inv = invoiceFilter.toLowerCase().trim();
    const s = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : null;
    const e = endDate ? new Date(endDate).setHours(23, 59, 59, 999) : null;
    return grns.filter(g => {
      if (paymentFilter && g.payment_status !== paymentFilter) return false;
      if (supplierFilter && g.supplier_id !== supplierFilter) return false;
      if (createdByFilter && g.created_by_name !== createdByFilter) return false;
      if (inv && !(g.invoice_number || '').toLowerCase().includes(inv)) return false;
      if (s || e) {
        const rd = g.received_date ? new Date(g.received_date).getTime() : new Date(g.created_at).getTime();
        if (s && rd < s) return false;
        if (e && rd > e) return false;
      }
      if (!q) return true;
      return (
        (g.grn_number || '').toLowerCase().includes(q) ||
        (g.supplier_name || '').toLowerCase().includes(q) ||
        (g.invoice_number || '').toLowerCase().includes(q)
      );
    });
  }, [grns, searchTerm, invoiceFilter, paymentFilter, supplierFilter, createdByFilter, startDate, endDate]);
  const clearFilters = () => {
    setSearchTerm(''); setInvoiceFilter(''); setPaymentFilter('');
    setSupplierFilter(''); setCreatedByFilter(''); setStartDate(''); setEndDate('');
  };
  const activeFilterCount = [supplierFilter, invoiceFilter, paymentFilter, startDate, endDate, createdByFilter, searchTerm].filter(Boolean).length;

  // === Supplier-detail computed rows ===
  const selectedBucket = useMemo(() => {
    if (!selectedSupplierId) return null;
    return supplierBuckets.find(r => r.key === selectedSupplierId || r.supplier_id === selectedSupplierId);
  }, [supplierBuckets, selectedSupplierId]);
  const supplierGRNs = useMemo(() => {
    if (!selectedBucket) return [];
    const rows = grns.filter(g => {
      if (selectedBucket.supplier_id) return g.supplier_id === selectedBucket.supplier_id;
      return (g.supplier_name || '').trim().toLowerCase() === (selectedBucket.supplier_name || '').trim().toLowerCase();
    });
    // Already sorted newest first from loadAll
    return rows;
  }, [grns, selectedBucket]);

  const openSupplier = (row) => {
    setSelectedSupplierId(row.key);
    setView('supplier-detail');
  };
  const backToSuppliers = () => {
    setSelectedSupplierId(null);
    setView('suppliers');
  };

  return (
    <div className="space-y-6" data-testid="grns-page">
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Goods Received Notes</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            {view === 'suppliers' && 'Browse GRNs grouped by supplier. Click any supplier to see their history.'}
            {view === 'all' && 'Flat list of every GRN across all suppliers.'}
            {view === 'supplier-detail' && `GRN history for ${selectedBucket?.supplier_name || 'supplier'}.`}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {/* View Toggle */}
          {view !== 'supplier-detail' && (
            <div className="inline-flex rounded-lg border border-[var(--border)] p-0.5 bg-[var(--surface)]" data-testid="view-toggle">
              <button
                onClick={() => setView('suppliers')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5 transition-colors ${view === 'suppliers' ? 'bg-blue-500 text-white' : 'text-[var(--text-muted)] hover:bg-[var(--muted)]'}`}
                data-testid="view-suppliers-btn">
                <UsersIcon size={13} /> Suppliers
              </button>
              <button
                onClick={() => setView('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5 transition-colors ${view === 'all' ? 'bg-blue-500 text-white' : 'text-[var(--text-muted)] hover:bg-[var(--muted)]'}`}
                data-testid="view-all-btn">
                <LayoutList size={13} /> All GRNs
              </button>
            </div>
          )}
          <Button onClick={handleNew} data-testid="new-grn-button">
            <Plus size={16} className="mr-1" /> New GRN
          </Button>
        </div>
      </div>

      {view === 'suppliers' && (
        <SuppliersView
          rows={filteredSupplierRows}
          totalCount={supplierBuckets.length}
          searchTerm={supplierSearch}
          onSearchChange={setSupplierSearch}
          onOpen={openSupplier}
          currency={currency}
        />
      )}

      {view === 'all' && (
        <AllGRNsView
          grns={grns}
          suppliers={suppliers}
          filteredGRNs={filteredGRNs}
          searchTerm={searchTerm} setSearchTerm={setSearchTerm}
          supplierFilter={supplierFilter} setSupplierFilter={setSupplierFilter}
          invoiceFilter={invoiceFilter} setInvoiceFilter={setInvoiceFilter}
          paymentFilter={paymentFilter} setPaymentFilter={setPaymentFilter}
          startDate={startDate} setStartDate={setStartDate}
          endDate={endDate} setEndDate={setEndDate}
          createdByFilter={createdByFilter} setCreatedByFilter={setCreatedByFilter}
          createdByOptions={createdByOptions}
          clearFilters={clearFilters}
          activeFilterCount={activeFilterCount}
          onEdit={handleEdit}
          onPrint={handlePrint}
          onDelete={handleDelete}
          isSuperAdmin={isSuperAdmin}
        />
      )}

      {view === 'supplier-detail' && selectedBucket && (
        <SupplierDetailView
          bucket={selectedBucket}
          grns={supplierGRNs}
          onBack={backToSuppliers}
          onEdit={handleEdit}
          onPrint={handlePrint}
          onDelete={handleDelete}
          isSuperAdmin={isSuperAdmin}
          currency={currency}
        />
      )}

      <GRNForm isOpen={showForm} onClose={() => setShowForm(false)} grn={editingGRN} onSaved={loadAll} />
    </div>
  );
};

// ==================== Suppliers Overview ====================
const SuppliersView = ({ rows, totalCount, searchTerm, onSearchChange, onOpen, currency }) => (
  <Card>
    <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
      <div className="relative flex-1 min-w-[240px] max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
        <input
          type="text"
          placeholder="Search supplier name, code, contact…"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full pl-9 pr-3 py-2 rounded-lg border border-[var(--border)] bg-[var(--surface)] focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="suppliers-view-search"
        />
      </div>
      <div className="text-xs text-[var(--text-muted)] flex items-center gap-1">
        <Filter size={12} /> Showing <strong>{rows.length}</strong> of {totalCount} suppliers · sorted by most GRNs first
      </div>
    </div>

    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Supplier ID</TableHead>
          <TableHead>Supplier Name</TableHead>
          <TableHead>Contact</TableHead>
          <TableHead className="text-right">Total GRNs</TableHead>
          <TableHead className="text-right">Items Received</TableHead>
          <TableHead className="text-right">Purchase Value</TableHead>
          <TableHead>Last Received</TableHead>
          <TableHead></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={8} className="text-center py-10 text-gray-500">
              No suppliers found. Create a GRN with a new supplier or add one from the Suppliers page.
            </TableCell>
          </TableRow>
        ) : (
          rows.map(row => (
            <TableRow
              key={row.key}
              className="cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/10"
              onClick={() => onOpen(row)}
              data-testid={`supplier-bucket-${row.key}`}
            >
              <TableCell className="font-mono text-xs font-semibold text-blue-600 dark:text-blue-400">
                {row.supplier_code || <span className="text-gray-400 italic">unlinked</span>}
              </TableCell>
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  <Building2 size={14} className="text-blue-500 shrink-0" />
                  <span>{row.supplier_name}</span>
                </div>
              </TableCell>
              <TableCell className="text-xs">
                {row.contact_person || row.phone
                  ? <span>{[row.contact_person, row.phone].filter(Boolean).join(' · ')}</span>
                  : <span className="text-gray-400">—</span>}
              </TableCell>
              <TableCell className="text-right">
                <Badge variant={row.grn_count > 0 ? 'info' : 'default'}>{row.grn_count}</Badge>
              </TableCell>
              <TableCell className="text-right font-semibold">{row.total_units}</TableCell>
              <TableCell className="text-right font-semibold text-emerald-600 dark:text-emerald-400">
                {formatCurrency(row.total_value, currency)}
              </TableCell>
              <TableCell className="text-xs">{row.last_received || <span className="text-gray-400">—</span>}</TableCell>
              <TableCell><ChevronRight size={14} className="text-[var(--text-muted)]" /></TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </Card>
);

// ==================== Supplier Detail (drill-in) ====================
const SupplierDetailView = ({ bucket, grns, onBack, onEdit, onPrint, onDelete, isSuperAdmin, currency }) => (
  <div className="space-y-4" data-testid="supplier-detail-view">
    <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-to-suppliers">
      <ArrowLeft size={14} className="mr-1" /> Back to Suppliers
    </Button>

    <Card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Building2 size={20} className="text-blue-500" />
            <h2 className="text-2xl font-bold">{bucket.supplier_name}</h2>
            {bucket.supplier_code && (
              <span className="text-xs font-mono text-blue-600 dark:text-blue-400 font-semibold px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30" data-testid="supplier-detail-code">
                {bucket.supplier_code}
              </span>
            )}
          </div>
          {(bucket.contact_person || bucket.phone) && (
            <div className="text-xs text-[var(--text-muted)] mt-1">
              {[bucket.contact_person, bucket.phone].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
        <div className="grid grid-cols-3 gap-3 min-w-[360px]" data-testid="supplier-detail-stats">
          <Stat label="Total GRNs" value={bucket.grn_count} />
          <Stat label="Items Received" value={bucket.total_units} />
          <Stat label="Purchase Value" value={formatCurrency(bucket.total_value, currency)} highlight />
        </div>
      </div>
    </Card>

    <Card>
      <div className="flex items-center gap-2 mb-3 text-xs text-[var(--text-muted)]">
        <Package size={12} /> {grns.length} GRN{grns.length === 1 ? '' : 's'} · sorted newest first
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>GRN #</TableHead>
            <TableHead>Supplier Invoice</TableHead>
            <TableHead>Items</TableHead>
            <TableHead>Received Date</TableHead>
            <TableHead>Due Date</TableHead>
            <TableHead>Payment</TableHead>
            <TableHead>Created By</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {grns.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                No GRNs recorded yet for this supplier.
              </TableCell>
            </TableRow>
          ) : (
            grns.map(grn => (
              <TableRow key={grn.id} data-testid={`grn-row-${grn.id}`}>
                <TableCell className="font-mono text-xs font-semibold">
                  {grn.grn_number || <span className="text-gray-400">—</span>}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {grn.invoice_number
                    ? grn.invoice_number
                    : <span className="italic text-gray-400" data-testid={`grn-no-invoice-${grn.id}`}>No Supplier Invoice</span>}
                </TableCell>
                <TableCell>{grn.items?.length || 0}</TableCell>
                <TableCell>{grn.received_date}</TableCell>
                <TableCell>{grn.due_date || '-'}</TableCell>
                <TableCell><Badge variant={getStatusVariant(grn.payment_status)}>{grn.payment_status}</Badge></TableCell>
                <TableCell className="text-xs text-[var(--text-muted)]">{grn.created_by_name || '—'}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => onEdit(grn)} data-testid={`edit-grn-${grn.id}`}>
                      <Edit size={16} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => onPrint(grn)} data-testid={`print-grn-${grn.id}`}>
                      <Printer size={16} />
                    </Button>
                    {isSuperAdmin && isSuperAdmin() && (
                      <Button size="sm" variant="ghost" onClick={() => onDelete(grn)} data-testid={`delete-grn-${grn.id}`}>
                        <Trash size={16} className="text-red-600" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Card>
  </div>
);

// ==================== Flat All-GRNs View ====================
const AllGRNsView = ({
  grns, suppliers, filteredGRNs,
  searchTerm, setSearchTerm, supplierFilter, setSupplierFilter, invoiceFilter, setInvoiceFilter,
  paymentFilter, setPaymentFilter, startDate, setStartDate, endDate, setEndDate,
  createdByFilter, setCreatedByFilter, createdByOptions,
  clearFilters, activeFilterCount, onEdit, onPrint, onDelete, isSuperAdmin,
}) => (
  <Card>
    <div className="mb-4 space-y-3" data-testid="grns-filter">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px] relative">
          <label className="text-xs font-medium mb-1 block">Search</label>
          <Search size={14} className="absolute left-3 top-8 text-[var(--text-muted)]" />
          <input
            type="text"
            placeholder="Search GRN #, supplier or invoice…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-[var(--border)] bg-[var(--surface)] focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="grns-search"
          />
        </div>
        <div className="w-56">
          <Select label="Supplier" value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
            data-testid="filter-supplier">
            <option value="">All suppliers</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>
              {s.supplier_code ? `${s.supplier_code} · ${s.name}` : s.name}
            </option>)}
          </Select>
        </div>
        <div className="w-48">
          <Input label="Supplier Invoice" placeholder="e.g. INV-5689" value={invoiceFilter}
            onChange={(e) => setInvoiceFilter(e.target.value)} data-testid="filter-invoice" />
        </div>
        <div className="w-40">
          <Select label="Payment" value={paymentFilter}
            onChange={(e) => setPaymentFilter(e.target.value)}
            data-testid="filter-payment">
            <option value="">All statuses</option>
            {PAYMENT_STATUSES.map(p => <option key={p} value={p}>{p}</option>)}
          </Select>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div><Input label="From" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="filter-start-date" /></div>
        <div><Input label="To" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="filter-end-date" /></div>
        <div className="w-48">
          <Select label="Created By" value={createdByFilter}
            onChange={(e) => setCreatedByFilter(e.target.value)}
            data-testid="filter-created-by">
            <option value="">Anyone</option>
            {createdByOptions.map(n => <option key={n} value={n}>{n}</option>)}
          </Select>
        </div>
        <div className="flex gap-2 ml-auto">
          {activeFilterCount > 0 && (
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters">
              <X size={14} className="mr-1" /> Clear ({activeFilterCount})
            </Button>
          )}
          <Button variant="secondary" size="sm"
            onClick={() => exportToCSV(filteredGRNs, 'grns', CSV_COLUMNS.grns)}
            data-testid="export-grns">
            <Download size={14} className="mr-1" /> Export
          </Button>
        </div>
      </div>
    </div>

    <div className="text-xs text-[var(--text-muted)] mb-2 flex items-center gap-1">
      <Filter size={12} /> Showing <strong>{filteredGRNs.length}</strong> of {grns.length} GRNs · sorted newest first
    </div>

    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>GRN #</TableHead>
          <TableHead>Supplier</TableHead>
          <TableHead>Supplier Invoice</TableHead>
          <TableHead>Items</TableHead>
          <TableHead>Received Date</TableHead>
          <TableHead>Due Date</TableHead>
          <TableHead>Payment</TableHead>
          <TableHead>Created By</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {filteredGRNs.length === 0 ? (
          <TableRow>
            <TableCell colSpan={9} className="text-center py-8 text-gray-500">
              {grns.length === 0 ? 'No GRNs yet. Click "New GRN" to create one.' : 'No GRNs match the filters.'}
            </TableCell>
          </TableRow>
        ) : (
          filteredGRNs.map((grn) => (
            <TableRow key={grn.id} data-testid={`grn-row-${grn.id}`}>
              <TableCell className="font-mono text-xs font-semibold">
                {grn.grn_number || <span className="text-gray-400">—</span>}
              </TableCell>
              <TableCell className="font-medium">
                <div>{grn.supplier_name}</div>
                {(() => {
                  const supplier = suppliers.find(s => s.id === grn.supplier_id);
                  return supplier?.supplier_code
                    ? <div className="text-[10px] text-[var(--text-muted)] font-mono">{supplier.supplier_code}</div>
                    : null;
                })()}
              </TableCell>
              <TableCell className="font-mono text-xs">
                {grn.invoice_number
                  ? grn.invoice_number
                  : <span className="italic text-gray-400" data-testid={`grn-no-invoice-${grn.id}`}>No Supplier Invoice</span>}
              </TableCell>
              <TableCell>{grn.items?.length || 0}</TableCell>
              <TableCell>{grn.received_date}</TableCell>
              <TableCell>{grn.due_date || '-'}</TableCell>
              <TableCell><Badge variant={getStatusVariant(grn.payment_status)}>{grn.payment_status}</Badge></TableCell>
              <TableCell className="text-xs text-[var(--text-muted)]">{grn.created_by_name || '—'}</TableCell>
              <TableCell>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => onEdit(grn)} data-testid={`edit-grn-${grn.id}`}>
                    <Edit size={16} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => onPrint(grn)} data-testid={`print-grn-${grn.id}`}>
                    <Printer size={16} />
                  </Button>
                  {isSuperAdmin && isSuperAdmin() && (
                    <Button size="sm" variant="ghost" onClick={() => onDelete(grn)} data-testid={`delete-grn-${grn.id}`}>
                      <Trash size={16} className="text-red-600" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </Card>
);

const Stat = ({ label, value, highlight }) => (
  <div className={`rounded-lg border p-3 ${highlight ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-[var(--border)] bg-[var(--surface)]'}`}>
    <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">{label}</div>
    <div className="text-lg font-bold">{value}</div>
  </div>
);

export default GRNs;
